import React, { useContext } from 'react';
import CreateCaseForm from './CreateCaseForm';
import DDMAttributesProvider from 'src/integration/ddm/providers/DDMAttributesProvider';
import { useGetSchemaQuery } from 'src/state/slice/ddmServiceApi';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { KatAlert, KatSpinner } from '@amzn/katal-react';

import { logger } from 'src/logger';
import isEmpty from 'lodash/isEmpty';
import { DDM_CPR_CLIENT_ID, PAGE_SCOPE } from 'src/constants/GlobalConstants';
import { PageContext } from 'src/state/providers/PageProvider';
import { DDMSchemaParser } from 'src/integration/ddm/DDMSchemaParser';
import { TranslationContext } from 'src/state/providers/TranslationProvider';

/**
 * This component is responsible to fetch the DDM Schema and provide it to the child components.
 */
const CreateCase = () => {
  const { preferredAccountId, customerId, ddmIntakeFormSchemaId } =
    useContext(AuthContext);
  const pageContext = useContext(PageContext);
  const bundles = useContext(TranslationContext);
  const { isLoading, data, isError, error } = useGetSchemaQuery({
    schemaKey: ddmIntakeFormSchemaId,
    clientId: DDM_CPR_CLIENT_ID,
    isPublicPage: PAGE_SCOPE.PUBLIC === pageContext?.pageScope,
  });

  if (isLoading) {
    return <KatSpinner data-testid="createCaseLoader" />;
  }

  if (isError) {
    logger.error(
      `Unable to fetch schema for user: ${customerId} with accountId: ${preferredAccountId} Check details in context:   ${JSON.stringify(
        error,
      )}`,
    );
    return (
      <KatAlert
        data-testid="createCaseErrorMesage"
        variant="danger"
        title="Error"
        description="Unable to render case creation page at this time."
        persistent
      />
    );
  }

  if (isEmpty(data) || isEmpty(data.schemaDefinition)) {
    logger.error(
      `Invalid schema for user: ${customerId} with accountId: ${preferredAccountId} Check details in context:`,
      data,
    );
    return (
      <KatAlert
        variant="danger"
        title="Error"
        description="No Schema defined for the account."
        persistent
      />
    );
  }

  const ddmSchema = new DDMSchemaParser(data, bundles);
  return (
    <DDMAttributesProvider
      createCaseSchema={ddmSchema.getCreateCaseSchema()}
      formData={ddmSchema.getDefaultFormData()}
      createCaseUiSchema={ddmSchema.getCreateCaseUiSchema()}
      createCaseStaticSchema={ddmSchema.getCreateCaseStaticSchema()}
      caseSummarySchema={ddmSchema.getCaseSummarySchema()}
      caseSummaryUiSchema={ddmSchema.getCaseSummaryUiSchema()}
      caseSummaryFormData={ddmSchema.getCaseSummaryFormData()}
      ddmSchemaId={ddmSchema.getSchemaId()}
      ddmSchemaVersion={ddmSchema.getSchemaVersion()}
    >
      <CreateCaseForm />
    </DDMAttributesProvider>
  );
};

export default CreateCase;
